import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { IconButton } from '@atlaskit/button/new';
import RetryIcon from '@atlaskit/icon/core/retry';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';

import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { fg } from '@confluence/feature-gating';

import type { SmartSpaceOverviewData } from '../SmartSpaceOverview.tsx';

import { QuestionItem, useRegenerateLoadingState } from './QuestionItem.tsx';
import { MOCK_DATA } from './mockData.ts';

export type FAQData = NonNullable<SmartSpaceOverviewData['faq']>;

type FAQEntry = NonNullable<FAQData['entries']>[number];

export type Source = FAQEntry['sources'][number];

type PopularQuestionsProps = {
	faqData: FAQData | null | undefined;
	spaceKey: string;
};

export const PopularQuestions = ({ faqData, spaceKey }: PopularQuestionsProps) => {
	const { formatMessage } = useIntl();
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [startRegenerateAll, isRegeneratingAll] = useRegenerateLoadingState();
	const publish = usePublish('ai-mate');

	const data = fg('smart-space-overview-faq-use-backend-query')
		? faqData
		: MOCK_DATA?.confluence_smartSpaceOverview?.faq;

	const handleToggle = useCallback((id: string) => {
		setExpandedId((prev) => (prev === id ? null : id));
	}, []);

	const formatSourcesContext = useCallback(
		(sources: readonly Source[]) => {
			if (sources.length === 0) {
				return '';
			}
			const sourceLines = sources.map(
				(source) =>
					`- [${source?.content?.title}](${window.location.origin}${source?.content?.links?.webui})`,
			);
			return `\n\n${formatMessage(i18n.sourcesLabel)}\n${sourceLines.join('\n')}`;
		},
		[formatMessage],
	);

	const createSpaceContext = useCallback(
		(parentQuestion: FAQEntry) => {
			const spaceOverviewUrl = `${window.location.origin}${SPACE_OVERVIEW.toUrl({ spaceKey })}`;

			const humanMessageContent = formatMessage(i18n.humanMessagePrompt, {
				link: spaceOverviewUrl,
				question: parentQuestion.question,
			});
			const agentMessageContent = `${parentQuestion.answer}${formatSourcesContext(parentQuestion.sources)}`;

			return {
				human_message: {
					content: humanMessageContent,
				},
				agent_message: {
					content: agentMessageContent,
				},
			};
		},
		[spaceKey, formatSourcesContext, formatMessage],
	);

	const publishRovoChat = useCallback(
		(questionText: string, parentQuestion: FAQEntry) => {
			const context = createSpaceContext(parentQuestion);

			publish({
				type: 'chat-new',
				source: 'space-overview-popular-questions',
				data: {
					name: questionText.substring(0, 30),
					dialogues: [context],
					prompt: questionText,
				},
			});
		},
		[publish, createSpaceContext],
	);

	const handleFollowUp = useCallback(
		(followUpQuestion: string, parentQuestion: FAQEntry) =>
			publishRovoChat(followUpQuestion, parentQuestion),
		[publishRovoChat],
	);

	const handleAskRovo = useCallback(
		(customQuestion: string, parentQuestion: FAQEntry) =>
			publishRovoChat(customQuestion, parentQuestion),
		[publishRovoChat],
	);

	const handleRegenerateAll = useCallback(() => {
		// TODO: Implement regenerate all questions
		setExpandedId(null);
		startRegenerateAll();
	}, [startRegenerateAll]);

	if ((data?.entries?.length ?? 0) > 0) {
		return (
			<section css={styles.container} aria-label={formatMessage(i18n.heading)}>
				<Stack space="space.150">
					<div css={styles.headingRow}>
						<Heading size="medium" as="h2">
							{formatMessage(i18n.heading)}
						</Heading>
						<div
							css={[
								styles.regenerateAllButton,
								isRegeneratingAll && styles.regenerateAllButtonVisible,
							]}
						>
							<IconButton
								icon={RetryIcon}
								label={formatMessage(i18n.regenerateAll)}
								appearance="subtle"
								spacing="compact"
								isLoading={isRegeneratingAll}
								onClick={handleRegenerateAll}
								isTooltipDisabled={false}
							/>
						</div>
					</div>
					<div css={[isRegeneratingAll && styles.contentBlurred]}>
						<Stack space="space.150">
							{data?.entries?.map((question) => {
								const questionKey = question.question;
								return (
									<QuestionItem
										key={questionKey}
										question={question}
										isExpanded={expandedId === questionKey}
										onToggle={handleToggle}
										onFollowUp={handleFollowUp}
										onAskRovo={handleAskRovo}
									/>
								);
							})}
						</Stack>
					</div>
				</Stack>
			</section>
		);
	}

	return null;
};

const i18n = defineMessages({
	heading: {
		id: 'space-overview.smart-space-overview.popular-questions.heading',
		defaultMessage: 'Popular questions',
		description: 'Heading for the popular questions section on smart space overview.',
	},
	humanMessagePrompt: {
		id: 'space-overview.smart-space-overview.popular-questions.human-message-prompt',
		defaultMessage: 'Question about space {link}: {question}',
		description: 'The initial human prompt sent to the chat when a popular question is selected.',
	},
	sourcesLabel: {
		id: 'space-overview.smart-space-overview.popular-questions.sources-label',
		defaultMessage: 'Sources:',
		description: 'Label for the sources section in the agent response.',
	},
	regenerateAll: {
		id: 'space-overview.smart-space-overview.popular-questions.regenerate-all',
		defaultMessage: 'Regenerate all questions',
		description: 'Label for the button that regenerates all popular questions.',
	},
});

const styles = cssMap({
	container: {
		maxWidth: '830px',
	},
	headingRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		'--regenerate-all-opacity': '0',
		'&:hover': {
			// @ts-expect-error -- CSS custom property not supported by cssMap types
			'--regenerate-all-opacity': '1',
		},
	},
	regenerateAllButton: {
		transition: 'opacity 150ms ease-in',
		// @ts-expect-error -- CSS custom property not supported by cssMap types
		opacity: 'var(--regenerate-all-opacity)',
	},
	regenerateAllButtonVisible: {
		opacity: '1',
	},
	contentBlurred: {
		opacity: token('opacity.disabled'),
		transition: 'opacity 150ms ease-in',
		pointerEvents: 'none',
	},
});
